import { normalizeMaterialKey, type MaterialKey } from '../constants/zeroIcons';
import type { PickupOrderListItem } from '../types/pickupOrder.type';
import { localDateKey } from './serverTime';

/**
 * kg per unit, as the API converts order amounts for the dashboard
 * (`pickup_order/helpers/weight_kg.py`). Units it does not convert
 * (cubic yards, gallons) and unknown units count as kg there too.
 */
const KG_PER_UNIT: Record<string, number> = {
  kg: 1,
  tons: 1000,
  lbs: 0.453592,
  bags: 13.6,
  cartons: 0.5,
  bottles: 0.025,
  pallets: 680,
  cubic_meters: 400,
  liters: 0.4,
};

export function orderAmountKg(amount: number, unit: string | null | undefined): number {
  if (!Number.isFinite(amount)) return 0;
  return amount * (KG_PER_UNIT[(unit ?? '').trim().toLowerCase()] ?? 1);
}

/**
 * The month as the dashboard counts it: orders created from the 1st 00:00 to
 * the last instant of the month, in the API's naive UTC (no `Z`, so the
 * server compares naive to naive like the dashboard does).
 */
export function monthOrderRange(monthStart: Date): { start: string; end: string; prefix: string } {
  const y = monthStart.getFullYear();
  const m = monthStart.getMonth();
  const pad = (n: number) => String(n).padStart(2, '0');
  const lastDay = new Date(y, m + 1, 0).getDate();
  const prefix = `${y}-${pad(m + 1)}`;
  return {
    start: `${prefix}-01T00:00:00`,
    end: `${prefix}-${pad(lastDay)}T23:59:59.999999`,
    prefix,
  };
}

/** Day keys of the month up to today (never future days). */
export function monthDayKeys(monthStart: Date, today: string = localDateKey(new Date())): string[] {
  const keys: string[] = [];
  const cursor = new Date(monthStart.getFullYear(), monthStart.getMonth(), 1);
  const month = cursor.getMonth();
  while (cursor.getMonth() === month) {
    const key = localDateKey(cursor);
    if (key > today) break;
    keys.push(key);
    cursor.setDate(cursor.getDate() + 1);
  }
  return keys;
}

export const REST_SERIES_KEY = '__rest';

export interface MaterialSeries {
  /** Recharts data key (`s0`, `s1`, … or {@link REST_SERIES_KEY}). */
  key: string;
  /** The organisation's material name, or null for the folded remainder. */
  name: string | null;
  category: MaterialKey;
  totalKg: number;
  /** Material names folded into the remainder series. */
  folded?: string[];
}

export interface MaterialDailySplit {
  series: MaterialSeries[];
  /** One row per day: `{ date, s0: kg, s1: kg, … }`. */
  rows: Array<Record<string, number | string>>;
  totalKg: number;
  orderCount: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Requested kg per day and material, built from the month's pickup orders the
 * same way the dashboard's daily total is: every status, bucketed by the UTC
 * creation day, converted to kg. Materials beyond `maxSeries - 1` are folded
 * into one remainder series so the stack stays readable.
 */
export function buildMaterialDailySplit(
  orders: readonly PickupOrderListItem[],
  monthStart: Date | null,
  maxSeries = 8,
  today?: string,
): MaterialDailySplit {
  const prefix = monthStart ? monthOrderRange(monthStart).prefix : null;
  const seen = new Set<string>();
  const byMaterial = new Map<string, { name: string; category: MaterialKey; totalKg: number; days: Map<string, number> }>();
  let orderCount = 0;

  for (const order of orders) {
    if (!order?.id || seen.has(order.id)) continue;
    const day = (order.created_at ?? '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) continue;
    if (prefix && !day.startsWith(prefix)) continue;
    seen.add(order.id);
    orderCount += 1;
    const kg = orderAmountKg(order.amount, order.amount_unit);
    const name = (order.material_type?.name || order.material_name_snapshot || '').trim() || '—';
    const category = normalizeMaterialKey(order.material_type?.parent_category ?? order.category_name_snapshot);
    const id = `${category}::${name.toLowerCase()}`;
    const entry = byMaterial.get(id) ?? { name, category, totalKg: 0, days: new Map<string, number>() };
    entry.totalKg += kg;
    entry.days.set(day, (entry.days.get(day) ?? 0) + kg);
    byMaterial.set(id, entry);
  }

  const ranked = [...byMaterial.values()].filter((m) => m.totalKg > 0).sort((a, b) => b.totalKg - a.totalKg);
  const keep = ranked.length > maxSeries ? ranked.slice(0, Math.max(maxSeries - 1, 1)) : ranked;
  const rest = ranked.slice(keep.length);

  const series: MaterialSeries[] = keep.map((m, i) => ({
    key: `s${i}`,
    name: m.name,
    category: m.category,
    totalKg: round2(m.totalKg),
  }));
  if (rest.length > 0) {
    series.push({
      key: REST_SERIES_KEY,
      name: null,
      category: 'other',
      totalKg: round2(rest.reduce((sum, m) => sum + m.totalKg, 0)),
      folded: rest.map((m) => m.name),
    });
  }

  const dayKeys = new Set<string>(monthStart ? monthDayKeys(monthStart, today) : []);
  for (const m of ranked) for (const day of m.days.keys()) dayKeys.add(day);

  const rows = [...dayKeys].sort().map((date) => {
    const row: Record<string, number | string> = { date };
    keep.forEach((m, i) => {
      row[`s${i}`] = round2(m.days.get(date) ?? 0);
    });
    if (rest.length > 0) {
      row[REST_SERIES_KEY] = round2(rest.reduce((sum, m) => sum + (m.days.get(date) ?? 0), 0));
    }
    return row;
  });

  return {
    series,
    rows,
    totalKg: round2(ranked.reduce((sum, m) => sum + m.totalKg, 0)),
    orderCount,
  };
}
